import type { ImportMappingResponse } from '@/services/importMappingsApi.ts';
import type { StudyForm } from '@/types/studyForms';
import type { CsvFile } from '@/types/csvFiles';

export interface FileMapping {
  file_name: string;
  form_name: string;
  csv_file_id?: number;
  study_form_id?: number;
  match_type?: string;
  status?: 'success' | 'failed' | 'partial' | 'pending';
  records_processed?: number;
  records_total?: number;
  records_success?: number;
  records_failed?: number;
  column_mappings?: Array<{
    column_name: string;
    mapping_type: 'metadata' | 'formField' | 'ignored';
    metadata_type?: string | null;
    target_field?: string;
  }>;
  errors?: Array<{
    row?: number;
    column?: string;
    message: string;
    severity: 'error' | 'warning';
  }>;
  file_error?: string;
}

interface RawApiData {
  id: number;
  name?: string;
  status?: string;
  created_at: string;
  file_mappings?: FileMapping[];
  processing_started_at?: string;
  completed_at?: string;
  failed_at?: string;
  records_processed?: number;
  total_records?: number;
  progress_percentage?: number;
  success_count?: number;
  error_count?: number;
  error_message?: string;
  [key: string]: unknown;
}

export interface MappingData {
  id: number;
  name: string;
  status: string;
  created_at: string;
  updated_at?: string;
  created_by?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  description: string;
  file_mappings: FileMapping[];
  processing_started_at?: string;
  completed_at?: string;
  failed_at?: string;
  records_processed?: number;
  total_records?: number;
  progress_percentage?: number;
  success_count?: number;
  error_count?: number;
  error_message?: string;
  import_validation_errors?: {
    [studyFormId: string]: string;
  } | {
    forms?: Record<string, string>;
    files?: Record<string, string>;
  } | Record<string, unknown>;
  _calculatedStats?: {
    filesCount: number;
    formsCount: number;
    columnMappingsCount: number;
  };
  _apiData?: RawApiData;
}

// Helper function to get status description
function getStatusDescription(
  status: string,
  data?: Partial<MappingData>
): string {
  switch (status) {
    case 'created':
      return 'Mapping created and ready for processing';
    case 'valid_for_import':
      return 'Mapping validated successfully and ready for import';
    case 'invalid_for_import':
      {
        const errorCount = data?.import_validation_errors
          ? typeof data.import_validation_errors === 'object' &&
            'forms' in data.import_validation_errors
            ? Object.keys(data.import_validation_errors.forms || {}).length +
              (data.import_validation_errors.files
                ? Object.keys(data.import_validation_errors.files).length
                : 0)
            : Object.keys(data.import_validation_errors).length
          : 0;
        return `Mapping validation failed - ${errorCount} error${errorCount !== 1 ? 's' : ''} found`;
      }
    case 'uploaded':
      return 'Data has been uploaded successfully';
    case 'processing':
      return `Processing ${data?.records_processed || 0} of ${data?.total_records || 0} records`;
    case 'completed':
      return `Import completed successfully - ${data?.success_count || 0} records imported`;
    case 'failed':
      return `Import failed - ${data?.error_message || 'Unknown error'}`;
    default:
      return 'Mapping created and ready for processing';
  }
}

// Helper function to transform data
export function transformMappingData(
  rawData: ImportMappingResponse | RawApiData,
  formsMap: Map<number, StudyForm> = new Map(),
  filesMap: Map<number, CsvFile> = new Map()
): MappingData {
  const mappingResponse = rawData as ImportMappingResponse;
  const status =
    mappingResponse.status || (rawData as RawApiData).status || 'created';
  const apiData = rawData as RawApiData;

  // Create file mappings with real names and details
  const fileMappings: FileMapping[] =
    mappingResponse.file_to_form_mappings?.map(ftf => {
      const file = filesMap.get(ftf.csv_file_id);
      const form = formsMap.get(ftf.study_form_id);

      // Get column mappings for this file-form pair
      const columnMappings =
        mappingResponse.column_mappings?.filter(
          cm =>
            cm.csv_file_id === ftf.csv_file_id &&
            cm.study_form_id === ftf.study_form_id
        ) || [];

      return {
        file_name: file?.filename || `CSV File #${ftf.csv_file_id}`,
        form_name: form?.name || `Form #${ftf.study_form_id}`,
        csv_file_id: ftf.csv_file_id,
        study_form_id: ftf.study_form_id,
        match_type: ftf.match_type,
        ...(ftf.nested_path && { nested_path: ftf.nested_path }),
        column_mappings: columnMappings.map(cm => ({
          column_name: cm.column_name,
          mapping_type: cm.mapping_type as
            | 'metadata'
            | 'formField'
            | 'ignored',
          metadata_type: cm.metadata_type,
          target_field: cm.target_field,
        })),
      };
    }) ||
    apiData.file_mappings?.map(fm => ({
      file_name: fm.file_name,
      form_name: fm.form_name,
    })) ||
    [];

  // Calculate statistics from mapping data
  const uniqueFileIds = new Set(
    mappingResponse.file_to_form_mappings?.map(ftf => ftf.csv_file_id) || []
  );
  const uniqueFormIds = new Set(
    mappingResponse.file_to_form_mappings?.map(ftf => ftf.study_form_id) || []
  );
  const columnMappingsCount = mappingResponse.column_mappings?.length || 0;

  const mappingDataForDescription: Partial<MappingData> = {
    import_validation_errors: mappingResponse.import_validation_errors,
    records_processed: apiData.records_processed,
    total_records: apiData.total_records,
    success_count: apiData.success_count,
    error_message: apiData.error_message,
  };

  return {
    id: rawData.id,
    name: `Import Mapping ${rawData.id}`,
    status: status,
    created_at: rawData.created_at,
    updated_at: mappingResponse.updated_at,
    created_by: mappingResponse.created_by,
    description: getStatusDescription(status, mappingDataForDescription),
    file_mappings: fileMappings,
    processing_started_at: apiData.processing_started_at,
    completed_at: apiData.completed_at,
    failed_at: apiData.failed_at,
    records_processed: apiData.records_processed,
    total_records: apiData.total_records,
    progress_percentage: apiData.progress_percentage,
    success_count: apiData.success_count,
    error_count: apiData.error_count,
    error_message: apiData.error_message,
    // Include calculated statistics
    _calculatedStats: {
      filesCount: uniqueFileIds.size,
      formsCount: uniqueFormIds.size,
      columnMappingsCount: columnMappingsCount,
    },
    import_validation_errors: mappingResponse.import_validation_errors,
    // Include raw API data for debugging
    _apiData: {
      ...apiData,
      file_to_form_mappings: mappingResponse.file_to_form_mappings,
      column_mappings: mappingResponse.column_mappings,
      import_validation_errors: mappingResponse.import_validation_errors,
    },
  };
}

